import { CheckCircle } from 'lucide-react';

const Services = () => {
  const services = [
    {
      title: 'Custom Heat Treatment Lines',
      description:
        'Complete turnkey solutions from design to commissioning. Multi-zone conveyor systems with integrated quenching, tempering, and automated material handling.',
      features: ['Custom design', 'Multi-zone heating', 'Automated controls', 'Full installation'],
    },
    {
      title: 'Process Engineering',
      description:
        'Metallurgical consulting to optimize your heat treatment process. Temperature profiling, atmosphere selection, and cycle optimization.',
      features: ['Process optimization', 'Temperature profiling', 'Atmosphere design', 'Quality assurance'],
    },
    {
      title: 'Retrofit & Upgrades',
      description:
        'Modernize existing furnaces with new controls, improved insulation, and energy-efficient heating elements. Extend equipment life and reduce operating costs.',
      features: ['Control upgrades', 'Energy efficiency', 'Modern automation', 'Extended lifespan'],
    },
    {
      title: 'Maintenance & Support',
      description:
        'Comprehensive lifecycle support including preventive maintenance, spare parts supply, emergency repairs, and operator training.',
      features: ['24/7 support', 'Spare parts', 'Training programs', 'Preventive maintenance'],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-orange-500/20 border border-orange-500 rounded-full text-sm font-semibold mb-4">
              Services
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Comprehensive <span className="gradient-text">Heat Treatment Solutions</span>
            </h1>
            <p className="text-xl text-gray-300">
              From design and engineering to installation, commissioning, and lifetime support — we
              deliver complete thermal solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover-lift">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle className="text-orange-500 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding dark-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="gradient-text">Process</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              A proven methodology to deliver precisely what you need, on time and on budget.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Understand your requirements and constraints' },
              { step: '02', title: 'Design', desc: 'Create detailed engineering drawings and specifications' },
              { step: '03', title: 'Fabrication', desc: 'Build to exact specifications with quality checks' },
              { step: '04', title: 'Support', desc: 'Commission, train, and provide ongoing maintenance' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold gradient-text mb-4">{item.step}</div>
                <h4 className="text-xl font-bold mb-2">{item.title}</h4>
                <p className="text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Ready to discuss your project?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Share your requirements and we'll provide a detailed proposal within 48 hours.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 gradient-bg text-white font-bold rounded-full hover:shadow-xl transition-all duration-300"
            data-testid="services-cta-btn"
          >
            Get Started
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;